import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

import { LENIS_OPTIONS } from '@config/scroll';
import { useAppStore } from '@stores/index';

/** Attribute on <html> that freezes native scrolling (see _reset.scss). */
const LOCK_ATTRIBUTE = 'data-scroll-locked';

/**
 * Owns scrolling: Lenis smoothing, its link to ScrollTrigger, and locking.
 *
 * One clock: Lenis is advanced from GSAP's ticker rather than its own rAF
 * loop, so smoothed scroll and every ScrollTrigger read the same frame.
 *
 * Smoothing runs under reduced motion too, at the owner's request, as the
 * hero orbit and video do. It glides to where the visitor asked to go and
 * adds no motion of its own beyond that: no overshoot, no parallax.
 * Native scrolling remains for touch (see config/scroll.ts).
 */
class ScrollManager {
  private lenis: Lenis | null = null;
  private unsubscribers: (() => void)[] = [];

  private readonly tick = (time: number): void => {
    /* GSAP reports seconds; Lenis expects milliseconds. */
    this.lenis?.raf(time * 1000);
  };

  init(): void {
    if (this.unsubscribers.length > 0) return;

    /* Lenis needs a truthful clock. Lag smoothing would hand it a fake
       delta after a long frame and the page would lurch. */
    gsap.ticker.lagSmoothing(0);
    gsap.ticker.add(this.tick);

    this.setSmoothing(true);

    this.unsubscribers.push(
      useAppStore.subscribe(
        (state) => state.isMenuOpen,
        (open) => {
          this.setLocked(open);
        },
      ),
    );
  }

  /**
   * Brings an element to the top of the viewport, honouring its CSS
   * `scroll-margin-top` so it lands clear of the floating header.
   */
  scrollToElement(element: HTMLElement, options: { immediate?: boolean } = {}): void {
    const immediate = options.immediate ?? false;
    if (this.lenis) {
      const margin = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
      this.lenis.scrollTo(element, { offset: -margin, immediate, force: true });
    } else {
      element.scrollIntoView({ behavior: immediate ? 'instant' : 'smooth' });
    }
  }

  /** Jumps to the top without animating, for route changes. */
  resetToTop(): void {
    if (this.lenis) this.lenis.scrollTo(0, { immediate: true, force: true });
    else window.scrollTo(0, 0);
  }

  dispose(): void {
    for (const unsubscribe of this.unsubscribers) unsubscribe();
    this.unsubscribers = [];
    gsap.ticker.remove(this.tick);
    this.setSmoothing(false);
  }

  private setSmoothing(enabled: boolean): void {
    if (enabled && !this.lenis) {
      this.lenis = new Lenis({ ...LENIS_OPTIONS });
      /* Wrapped: ScrollTrigger.update is a static method, and passing it
         bare would detach it from its class. */
      this.lenis.on('scroll', () => {
        ScrollTrigger.update();
      });
    } else if (!enabled && this.lenis) {
      this.lenis.destroy();
      this.lenis = null;
    }
  }

  private setLocked(locked: boolean): void {
    document.documentElement.toggleAttribute(LOCK_ATTRIBUTE, locked);
    if (locked) this.lenis?.stop();
    else this.lenis?.start();
  }
}

export const scrollManager = new ScrollManager();

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    scrollManager.dispose();
  });
}
